/**
 * Admin Teachers Page
 *
 * Page for managing teachers (Director only).
 * Allows creating, editing, and deactivating teachers.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../components/ui";
import {
    useAdminClassGroups,
    useAdminTeachers,
    useCreateTeacher,
    useDeleteTeacher,
    useHardDeleteTeacher,
    useReactivateTeacher,
    useResetTeacherPassword,
    useUpdateTeacher,
} from "../../hooks/useAdmin";
import type {
    AdminTeacher,
    CreateTeacherRequest,
    UpdateTeacherRequest,
} from "../../types/admin";
import { applyPhoneMask } from "../../utils";

interface TeacherFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
}

const initialFormData: TeacherFormData = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  password: "",
};

export function AdminTeachersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const action = searchParams.get("action");
  const statusFilter = searchParams.get("status") || "active";

  const [selectedTeacher, setSelectedTeacher] = useState<AdminTeacher | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<TeacherFormData>(initialFormData);
  const [actionModal, setActionModal] = useState<{
    type: "deactivate" | "reactivate" | "delete";
    teacher: AdminTeacher;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTeacher, setExpandedTeacher] = useState<string | null>(null);
  const [resetPasswordModal, setResetPasswordModal] = useState<{
    teacher: AdminTeacher;
    newPassword?: string;
  } | null>(null);

  const { data: teachers, isLoading } = useAdminTeachers();
  const { data: classes } = useAdminClassGroups();

  const createTeacher = useCreateTeacher();
  const updateTeacher = useUpdateTeacher();
  const deleteTeacher = useDeleteTeacher();
  const reactivateTeacher = useReactivateTeacher();
  const hardDeleteTeacher = useHardDeleteTeacher();
  const resetPassword = useResetTeacherPassword();

  // Filter teachers by search and status
  const filteredTeachers = teachers?.filter((teacher) => {
    // Filter by status
    if (statusFilter === "active" && !teacher.isActive) return false;
    if (statusFilter === "inactive" && teacher.isActive) return false;

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        teacher.fullName.toLowerCase().includes(query) ||
        teacher.email.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Get classes for a specific teacher
  const getTeacherClasses = (teacherId: string) => {
    return classes?.filter((c) => c.teacherId === teacherId) || [];
  };

  // Open create modal if action=create
  useEffect(() => {
    if (action === "create") {
      handleOpenCreate();
      setSearchParams((prev) => {
        prev.delete("action");
        return prev;
      });
    }
  }, [action]);

  const handleOpenCreate = () => {
    setFormData(initialFormData);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (teacher: AdminTeacher) => {
    setFormData({
      email: teacher.email,
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      phone: teacher.phone || "",
      password: "",
    });
    setSelectedTeacher(teacher);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTeacher(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing && selectedTeacher) {
      const data: UpdateTeacherRequest = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
      };

      await updateTeacher.mutateAsync({ id: selectedTeacher.id, data });
    } else {
      const data: CreateTeacherRequest = {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || undefined,
        password: formData.password || undefined,
      };

      await createTeacher.mutateAsync(data);
    }

    handleCloseModal();
  };

  const handleAction = async (
    type: "deactivate" | "reactivate" | "delete",
    teacherId: string,
  ) => {
    if (type === "deactivate") {
      await deleteTeacher.mutateAsync(teacherId);
    } else if (type === "reactivate") {
      await reactivateTeacher.mutateAsync(teacherId);
    } else if (type === "delete") {
      await hardDeleteTeacher.mutateAsync(teacherId);
    }

    setActionModal(null);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordModal) return;
    const result = await resetPassword.mutateAsync(resetPasswordModal.teacher.id);
    setResetPasswordModal({
      ...resetPasswordModal,
      newPassword: result.newPassword,
    });
  };

  const handleStatusFilterChange = (status: string) => {
    setSearchParams((prev) => {
      if (status === "active") {
        prev.delete("status");
      } else {
        prev.set("status", status);
      }
      return prev;
    });
  };

  const toggleExpand = (teacherId: string) => {
    setExpandedTeacher(expandedTeacher === teacherId ? null : teacherId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Manage Teachers
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Add and manage teachers
              </p>
            </div>
            <button
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-user-add-line"></i>
              New Teacher
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-earth-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-earth-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        {/* Teachers List */}
        <div className="space-y-4">
          {filteredTeachers?.map((teacher) => {
            const teacherClasses = getTeacherClasses(teacher.id);
            const isExpanded = expandedTeacher === teacher.id;
            const isStaffUser = teacher.email.toLowerCase().includes("elienai");

            return (
              <div
                key={teacher.id}
                className={`bg-white rounded-xl border overflow-hidden ${!teacher.isActive ? "border-red-200 bg-red-50/30" : "border-earth-200"}`}
              >
                {/* Teacher Info */}
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-12 h-12 bg-olive-100 rounded-full flex items-center justify-center text-olive-700 font-semibold text-lg shrink-0">
                      {teacher.firstName.charAt(0)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-earth-800">
                              {teacher.fullName}
                            </h3>
                            {!teacher.isActive && (
                              <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs font-medium">
                                Inactive
                              </span>
                            )}
                            {isStaffUser && (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                Director
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-earth-700">
                            {teacher.email}
                          </p>
                          {teacher.phone && (
                            <p className="text-sm text-earth-700 flex items-center gap-1 mt-1">
                              <i className="ri-phone-line text-xs"></i>
                              {teacher.phone}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleOpenEdit(teacher)}
                            className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <i className="ri-edit-line text-earth-500"></i>
                          </button>
                          <button
                            onClick={() =>
                              setResetPasswordModal({
                                teacher,
                                newPassword: undefined,
                              })
                            }
                            className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Reset password"
                          >
                            <i className="ri-lock-password-line text-blue-600"></i>
                          </button>
                          {teacher.isActive ? (
                            <button
                              onClick={() =>
                                setActionModal({ type: "deactivate", teacher })
                              }
                              className="p-1.5 hover:bg-amber-50 rounded-lg transition-colors"
                              title="Deactivate"
                              disabled={isStaffUser}
                            >
                              <i
                                className={`ri-pause-circle-line ${isStaffUser ? "text-earth-300" : "text-amber-500"}`}
                              ></i>
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() =>
                                  setActionModal({
                                    type: "reactivate",
                                    teacher,
                                  })
                                }
                                className="p-1.5 hover:bg-green-50 rounded-lg transition-colors"
                                title="Reactivate"
                              >
                                <i className="ri-play-circle-line text-green-500"></i>
                              </button>
                              <button
                                onClick={() =>
                                  setActionModal({ type: "delete", teacher })
                                }
                                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete permanently"
                              >
                                <i className="ri-delete-bin-line text-red-500"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Classes Summary */}
                      <div className="mt-3 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-medium">
                          <i className="ri-group-line"></i>
                          {teacherClasses.length} class
                          {teacherClasses.length !== 1 && "es"}
                        </span>
                        <span className="text-xs text-earth-700">
                          {teacherClasses.reduce(
                            (acc, c) => acc + c.studentCount,
                            0,
                          )}{" "}
                          students total
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Classes Toggle */}
                {teacherClasses.length > 0 && (
                  <>
                    <button
                      onClick={() => toggleExpand(teacher.id)}
                      className="w-full px-4 py-2 bg-earth-50 hover:bg-earth-100 border-t border-earth-100 flex items-center justify-center gap-1 text-xs text-earth-700 transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <i className="ri-arrow-up-s-line"></i>
                          Ocultar turmas
                        </>
                      ) : (
                        <>
                          <i className="ri-arrow-down-s-line"></i>
                          Ver turmas ({teacherClasses.length})
                        </>
                      )}
                    </button>

                    {/* Classes List */}
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-2 border-t border-earth-100">
                        <div className="pt-3">
                          {teacherClasses.map((classGroup) => (
                            <Link
                              key={classGroup.id}
                              to={`/admin/classes?edit=${classGroup.id}`}
                              className="flex items-center justify-between p-3 bg-earth-50 hover:bg-earth-100 rounded-lg transition-colors mb-2"
                            >
                              <div>
                                <p className="font-medium text-earth-800 text-sm">
                                  {classGroup.name}
                                </p>
                                <p className="text-xs text-earth-700">
                                  {classGroup.levelCode} •{" "}
                                  {classGroup.schedule?.days?.join(", ") || ""}
                                  {classGroup.schedule?.time
                                    ? ` às ${classGroup.schedule.time}`
                                    : ""}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-earth-700">
                                  {classGroup.studentCount}/
                                  {classGroup.maxStudents}
                                </span>
                                <i className="ri-arrow-right-s-line text-earth-400"></i>
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredTeachers?.length === 0 && (
          <div className="bg-white rounded-xl border border-earth-200 text-center py-12">
            <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-user-star-line text-2xl text-earth-400"></i>
            </div>
            <p className="text-earth-600 font-medium">No teachers found</p>
            <p className="text-earth-700 text-sm mt-1">
              {searchQuery
                ? "Try a different search"
                : "Add a teacher to get started"}
            </p>
          </div>
        )}

        {/* Stats */}
        <div className="mt-4 text-sm text-earth-700">
          {filteredTeachers?.length || 0} teacher
          {(filteredTeachers?.length || 0) !== 1 ? "s" : ""} found
        </div>
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={handleCloseModal}
          ></div>
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Teacher" : "New Teacher"}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    placeholder="Maria"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    placeholder="Santos"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                  placeholder="teacher@email.com"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: applyPhoneMask(e.target.value) }))
                  }
                  maxLength={15}
                  placeholder="(81) 99999-9999"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                />
              </div>

              {/* Password (only for create) */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Password
                  </label>
                  <input
                    type="text"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    placeholder="Leave empty to use firstname123"
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  />
                  <p className="text-xs text-earth-700 mt-1">
                    Default password: first name in lowercase + 123
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createTeacher.isPending || updateTeacher.isPending}
                  className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {createTeacher.isPending || updateTeacher.isPending ? (
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size="sm" />
                      Saving...
                    </span>
                  ) : isEditing ? (
                    "Save Changes"
                  ) : (
                    "Create Teacher"
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Action Confirmation Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setActionModal(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6">
            <div className="text-center">
              {/* Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  actionModal.type === "delete"
                    ? "bg-red-100"
                    : actionModal.type === "deactivate"
                      ? "bg-amber-100"
                      : "bg-green-100"
                }`}
              >
                <i
                  className={`text-xl ${
                    actionModal.type === "delete"
                      ? "ri-delete-bin-line text-red-600"
                      : actionModal.type === "deactivate"
                        ? "ri-pause-circle-line text-amber-600"
                        : "ri-play-circle-line text-green-600"
                  }`}
                ></i>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-earth-800 mb-2">
                {actionModal.type === "delete"
                  ? "Delete Teacher Permanently?"
                  : actionModal.type === "deactivate"
                    ? "Deactivate Teacher?"
                    : "Reactivate Teacher?"}
              </h3>

              {/* Description */}
              <p className="text-sm text-earth-700 mb-2">
                <strong>{actionModal.teacher.fullName}</strong>
              </p>
              <p className="text-sm text-earth-700 mb-6">
                {actionModal.type === "delete"
                  ? "This action cannot be undone. The teacher and all associated data will be permanently removed."
                  : actionModal.type === "deactivate"
                    ? "The teacher will be deactivated and will no longer be able to access the portal."
                    : "The teacher will be reactivated and will regain access to the portal."}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    handleAction(actionModal.type, actionModal.teacher.id)
                  }
                  disabled={
                    deleteTeacher.isPending ||
                    reactivateTeacher.isPending ||
                    hardDeleteTeacher.isPending
                  }
                  className={`flex-1 px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                    actionModal.type === "delete"
                      ? "bg-red-600 hover:bg-red-700"
                      : actionModal.type === "deactivate"
                        ? "bg-amber-600 hover:bg-amber-700"
                        : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {deleteTeacher.isPending ||
                  reactivateTeacher.isPending ||
                  hardDeleteTeacher.isPending
                    ? "Processing..."
                    : actionModal.type === "delete"
                      ? "Delete Permanently"
                      : actionModal.type === "deactivate"
                        ? "Deactivate"
                        : "Reactivate"}
                </button>
                <button
                  onClick={() => setActionModal(null)}
                  className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setResetPasswordModal(null)}
          ></div>
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6">
            <div className="text-center">
              {/* Icon */}
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-password-line text-xl text-blue-600"></i>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-earth-800 mb-2">
                {resetPasswordModal.newPassword
                  ? "Password Reset Successfully!"
                  : "Reset Teacher Password?"}
              </h3>

              {/* Description */}
              <p className="text-sm text-earth-700 mb-2">
                <strong>{resetPasswordModal.teacher.fullName}</strong>
              </p>

              {!resetPasswordModal.newPassword ? (
                <>
                  <p className="text-sm text-earth-700 mb-2">
                    A senha será resetada para:
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                    <code className="text-blue-700 font-mono font-semibold">
                      {resetPasswordModal.teacher.firstName.toLowerCase()}123
                    </code>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-earth-700 mb-2">
                    Nova senha do professor:
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
                    <code className="text-green-700 font-mono font-semibold text-lg">
                      {resetPasswordModal.newPassword}
                    </code>
                  </div>
                  <p className="text-xs text-earth-700">
                    Certifique-se de copiar esta senha antes de fechar.
                  </p>
                </>
              )}

              {/* Actions */}
              <div className="flex items-center gap-3 mt-6">
                {!resetPasswordModal.newPassword ? (
                  <>
                    <button
                      onClick={handleResetPassword}
                      disabled={resetPassword.isPending}
                      className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {resetPassword.isPending ? "Resetting..." : "Reset Password"}
                    </button>
                    <button
                      onClick={() => setResetPasswordModal(null)}
                      className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setResetPasswordModal(null)}
                    className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTeachersPage;
